import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChatBubbleOvalLeftIcon as MessageCircle,
  XMarkIcon as X,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import supportChatbotService, {
  SupportChatbotMessage,
  SendMessageData,
} from "../../services/supportChatbotService";
import ChatbotInterface from "./ChatbotInterface";

// Utilidad para validar email
const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

interface Props {
  isSidebar?: boolean;
}
const SupportStickyButton: React.FC<Props> = ({ isSidebar = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<SupportChatbotMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [ticketStep, setTicketStep] = useState<
    null | "askEmail" | "askDesc" | "confirm"
  >(null);
  const [pendingTicket, setPendingTicket] = useState<{
    email: string;
    desc: string;
  }>({ email: "", desc: "" });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  // Mensaje de bienvenida inicial
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = t(
        "supportChatbot.welcome.public",
        "¡Hola! Soy el asistente virtual de VettConsult. ¿En qué puedo ayudarte hoy? Puedo informarte sobre nuestros servicios y ayudarte a registrarte."
      );
      const botMessage = supportChatbotService.createBotMessage(welcomeMessage);
      setMessages([botMessage]);
    }
  }, [isOpen, t]);

  // Scroll automático al final
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Detectar solicitud de ayuda humana
  const checkForHumanHelp = (msg: string) => {
    const triggers = [
      "ayuda humana",
      "soporte humano",
      "quiero hablar con soporte",
      "quiero ayuda de una persona",
      "human help",
      "human support",
      "real person",
      "contactar soporte",
      "contactar humano",
    ];
    return triggers.some((t) => msg.toLowerCase().includes(t));
  };

  // Enviar ticket al backend
  const sendSupportTicket = async (email: string, desc: string) => {
    try {
      setIsLoading(true);
      await supportChatbotService.sendSupportTicket({
        email,
        description: desc,
      });
      setTicketStep("confirm");
      setMessages((prev) => [
        ...prev,
        supportChatbotService.createBotMessage(
          t(
            "supportChatbot.ticket.confirm",
            "¡Listo! Tu ticket ha sido enviado a nuestro equipo de soporte. Te responderemos a la brevedad al correo proporcionado."
          )
        ),
      ]);
    } catch (error) {
      toast.error(
        t(
          "supportChatbot.ticket.error",
          "Hubo un error al enviar tu ticket. Intenta de nuevo más tarde."
        )
      );
      setMessages((prev) => [
        ...prev,
        supportChatbotService.createBotMessage(
          t(
            "supportChatbot.ticket.error",
            "Hubo un error al enviar tu ticket. Intenta de nuevo más tarde."
          )
        ),
      ]);
    } finally {
      setIsLoading(false);
      setTicketStep(null);
      setPendingTicket({ email: "", desc: "" });
    }
  };

  // Manejar envío de mensaje
  const handleSendMessage = async (message: string) => {
    if (!message.trim() || isLoading) return;

    // Flujo de ticket de soporte humano
    if (ticketStep === "askEmail") {
      if (!isValidEmail(message.trim())) {
        setMessages((prev) => [
          ...prev,
          supportChatbotService.createBotMessage(
            t(
              "supportChatbot.ticket.invalidEmail",
              "Por favor, ingresa un correo electrónico válido."
            )
          ),
        ]);
        return;
      }
      setPendingTicket((prev) => ({ ...prev, email: message.trim() }));
      setMessages((prev) => [
        ...prev,
        supportChatbotService.createUserMessage(message),
        supportChatbotService.createBotMessage(
          t(
            "supportChatbot.ticket.askDesc",
            "Gracias. Ahora, por favor, describe brevemente tu consulta o problema."
          )
        ),
      ]);
      setTicketStep("askDesc");
      return;
    }
    if (ticketStep === "askDesc") {
      setPendingTicket((prev) => ({ ...prev, desc: message.trim() }));
      setMessages((prev) => [
        ...prev,
        supportChatbotService.createUserMessage(message),
        supportChatbotService.createBotMessage(
          t(
            "supportChatbot.ticket.sending",
            "Enviando tu ticket al equipo de soporte..."
          )
        ),
      ]);
      // Enviar ticket
      await sendSupportTicket(pendingTicket.email, message.trim());
      return;
    }

    // Si el usuario pide ayuda humana
    if (checkForHumanHelp(message)) {
      setMessages((prev) => [
        ...prev,
        supportChatbotService.createUserMessage(message),
        supportChatbotService.createBotMessage(
          t(
            "supportChatbot.ticket.intro",
            "¡Por supuesto! Puedo ayudarte a crear un ticket de soporte humano. Por favor, indícame tu correo electrónico de contacto."
          )
        ),
      ]);
      setTicketStep("askEmail");
      return;
    }

    // Flujo normal IA
    try {
      setIsLoading(true);
      setIsTyping(true);
      // Agregar mensaje del usuario
      const userMessage = supportChatbotService.createUserMessage(message);
      setMessages((prev) => [...prev, userMessage]);
      // Enviar mensaje al backend
      const sendData: SendMessageData = {
        message,
        language: i18n.language,
      };
      const response = await supportChatbotService.sendPublicMessage(sendData);
      // Simular tiempo de escritura
      setTimeout(() => {
        setIsTyping(false);
        // Agregar respuesta del bot
        const botMessage = supportChatbotService.createBotMessage(
          response.message,
          response.needsEscalation,
          response.escalationEmailSent
        );
        setMessages((prev) => [...prev, botMessage]);
        // Mostrar notificaciones si es necesario
        if (response.needsEscalation && response.escalationEmailSent) {
          toast.success(
            t(
              "supportChatbot.escalation.success",
              "Tu consulta ha sido enviada al equipo de soporte. Te responderemos en las próximas 24 horas."
            ),
            { duration: 5000 }
          );
        }
      }, 1000 + Math.random() * 2000);
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error(
        t(
          "supportChatbot.error.send",
          "Error al enviar mensaje. Por favor, intenta de nuevo."
        ),
        { duration: 4000 }
      );
      // Agregar mensaje de error
      const errorMessage = supportChatbotService.createBotMessage(
        t(
          "supportChatbot.error.fallback",
          "Lo siento, estoy teniendo problemas técnicos. Por favor, intenta de nuevo en unos momentos."
        )
      );
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const buttonLabel = t("sidebar.supportChat", "Support Chat");

  const renderButton = () => {
    if (isSidebar) {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-white/80 hover:bg-[rgba(38,166,154,0.2)] hover:text-white w-full text-left"
        >
          <div className="text-xl">
            <MessageCircle />
          </div>
          <span className="font-medium">{buttonLabel}</span>
        </button>
      );
    } else {
      return (
        <AnimatePresence>
          {!isOpen && (
            <motion.div
              key="help-button-container"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              className="relative"
            >
              {/* Botón con LiquidGlass - IGUAL que FloatingAIChat */}
              <button
                onClick={() => setIsOpen(true)}
                className="w-14 h-14 flex items-center justify-center"
                style={{
                  position: "relative",
                  border: "2px solid rgba(0, 137, 123, 0.5)",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, rgba(38, 166, 154, 0.9), rgba(0, 137, 123, 0.85))",
                  backdropFilter: "blur(20px)",
                  boxShadow: `
                    0 8px 32px rgba(0, 137, 123, 0.5),
                    inset 0 1px 2px rgba(255, 255, 255, 0.3),
                    inset 0 -1px 1px rgba(255, 255, 255, 0.1)
                  `,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.1)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 40px rgba(0, 137, 123, 0.7), inset 0 1px 2px rgba(255, 255, 255, 0.3), inset 0 -1px 1px rgba(255, 255, 255, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 32px rgba(0, 137, 123, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.3), inset 0 -1px 1px rgba(255, 255, 255, 0.1)";
                }}
              >
                {/* Icono centrado */}
                <MessageCircle
                  className="w-7 h-7"
                  style={{
                    color: "#ffffff",
                    strokeWidth: 2.5,
                    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))",
                  }}
                />
                {/* Green dot indicator */}
                <div
                  style={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
                    border: "2px solid white",
                    boxShadow: "0 2px 8px rgba(34, 197, 94, 0.6)",
                  }}
                />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      );
    }
  };

  // Función para renderizar el modal del chatbot
  const renderChatModal = () => (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay de fondo */}
          <motion.div
            key="chatbot-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
            onClick={() => setIsOpen(false)}
          />

          <motion.div
            key="chatbot-panel"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="fixed inset-0 z-[9999] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden backdrop-blur-sm m-auto"
            style={{
              width: "min(95vw, 800px)",
              height: "80vh",
              maxWidth: "800px",
              maxHeight: "90vh",
              boxShadow:
                "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255,255,255,0.05)",
            }}
          >
            {/* Header with Liquidmorphism iOS style */}
            <div
              className="p-4 text-white relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, 
                  rgba(38, 166, 154, 0.95) 0%, 
                  rgba(0, 233, 190, 0.9) 50%, 
                  rgba(38, 166, 154, 0.85) 100%)`,
                backdropFilter: "blur(40px) saturate(180%)",
                WebkitBackdropFilter: "blur(40px) saturate(180%)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.18)",
                boxShadow: `
                  0 8px 32px 0 rgba(38, 166, 154, 0.37),
                  inset 0 1px 0 0 rgba(255, 255, 255, 0.3),
                  inset 0 -1px 0 0 rgba(255, 255, 255, 0.1)
                `,
              }}
            >
              {/* Liquidmorphism gradient overlay */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `
                    radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.2) 0%, transparent 50%),
                    radial-gradient(circle at 80% 80%, rgba(0, 233, 190, 0.3) 0%, transparent 50%)
                  `,
                  mixBlendMode: "overlay",
                  pointerEvents: "none",
                }}
              />

              {/* Animated liquid blobs */}
              <div
                style={{
                  position: "absolute",
                  top: "-50%",
                  left: "-20%",
                  width: "140%",
                  height: "200%",
                  background: `
                    radial-gradient(ellipse at center, rgba(255, 255, 255, 0.15) 0%, transparent 50%)
                  `,
                  filter: "blur(40px)",
                  animation: "liquidFloat 8s ease-in-out infinite",
                  pointerEvents: "none",
                }}
              />

              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Icon with liquid glass effect */}
                  <div
                    style={{
                      position: "relative",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.25)",
                      backdropFilter: "blur(10px)",
                      border: "1.5px solid rgba(255, 255, 255, 0.4)",
                      boxShadow: `
                        0 8px 16px rgba(0, 0, 0, 0.2),
                        inset 0 1px 0 rgba(255, 255, 255, 0.5),
                        inset 0 -1px 0 rgba(255, 255, 255, 0.2)
                      `,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MessageCircle
                      className="h-5 w-5"
                      style={{
                        filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))",
                      }}
                    />
                    {/* Green dot indicator */}
                    <div
                      style={{
                        position: "absolute",
                        top: "2px",
                        right: "2px",
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
                        border: "2px solid white",
                        boxShadow: "0 2px 8px rgba(34, 197, 94, 0.6)",
                      }}
                    />
                  </div>
                  <div>
                    <h3
                      className="font-bold text-base"
                      style={{
                        textShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                        letterSpacing: "0.3px",
                      }}
                    >
                      {t(
                        "supportChatbot.widget.title",
                        "VettConsult Assistant"
                      )}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs text-white/90 font-medium"
                        style={{
                          textShadow: "0 1px 4px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        {t("supportChatbot.status.online", "Online")} •{" "}
                        {t(
                          "supportChatbot.widget.info",
                          "Real-time response with specialized AI"
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full transition-all duration-200"
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(239, 68, 68, 0.3)";
                    e.currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.2)";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                  title={t("common.close", "Close")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            {/* Contenido del chatbot */}
            <div className="flex-1 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
              {/* Patrón de fondo sutil */}
              <div className="absolute inset-0 opacity-5">
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, rgba(38,166,154,0.3) 1px, transparent 0)`,
                    backgroundSize: "20px 20px",
                  }}
                />
              </div>
              <div className="relative h-full">
                <ChatbotInterface
                  messages={messages}
                  isLoading={isLoading}
                  isTyping={isTyping}
                  onSendMessage={handleSendMessage}
                  messagesEndRef={messagesEndRef}
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <div className={isSidebar ? "" : "fixed bottom-6 right-6 z-50"}>
      {renderButton()}

      {/* Renderizar el modal */}
      {isSidebar && typeof document !== "undefined"
        ? createPortal(renderChatModal(), document.body)
        : !isSidebar && renderChatModal()}
    </div>
  );
};

export default SupportStickyButton;
